import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Eye, Tag, FolderOpen } from 'lucide-react';

const BACKEND_URL = '';

/**
 * Single article detail page for tenant portals.
 */
export default function TenantArticle({ tenant, basePath }) {
  const { articleSlug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenant?.slug || !articleSlug) return;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/blog/${tenant.slug}/articles/${articleSlug}`)
      .then(r => r.json())
      .then(data => setArticle(data.article))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenant?.slug, articleSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-3xl mx-auto px-4 py-16 animate-pulse space-y-6">
          <div className="h-4 bg-stone-200 rounded w-24" />
          <div className="h-8 bg-stone-200 rounded w-3/4" />
          <div className="h-64 bg-stone-100 rounded-xl" />
          <div className="space-y-3">
            <div className="h-3 bg-stone-100 rounded w-full" />
            <div className="h-3 bg-stone-100 rounded w-5/6" />
            <div className="h-3 bg-stone-100 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-stone-900 mb-2">Article not found</h2>
          <Link to={`${basePath}/blog`} className="text-sm text-stone-500 hover:text-stone-700">
            Back to articles
          </Link>
        </div>
      </div>
    );
  }

  const brandColor = tenant?.branding?.primary_color || '#1a1a1a';

  return (
    <div className="min-h-screen bg-white" data-testid="tenant-article">
      {/* Header */}
      <div className="border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
          <Link
            to={`${basePath}/blog`}
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-700 transition-colors"
            data-testid="article-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to articles
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Meta */}
        <div className="flex items-center gap-3 mb-4 text-sm text-stone-500 flex-wrap">
          {article.category_slug && (
            <span
              className="text-xs font-medium px-2.5 py-1 rounded-full"
              style={{ backgroundColor: `${brandColor}15`, color: brandColor }}
            >
              <FolderOpen className="w-3 h-3 inline mr-1" />
              {article.category_slug}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {new Date(article.created_at).toLocaleDateString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric'
            })}
          </span>
          {article.views > 0 && (
            <span className="flex items-center gap-1">
              <Eye className="w-3.5 h-3.5" />
              {article.views} views
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4 leading-tight" data-testid="article-title">
          {article.title}
        </h1>

        {article.excerpt && (
          <p className="text-lg text-stone-500 mb-8">{article.excerpt}</p>
        )}

        {/* Featured Image */}
        {article.featured_image && (
          <div className="mb-8 rounded-xl overflow-hidden border border-stone-100">
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full h-auto"
              data-testid="article-featured-image"
            />
          </div>
        )}

        {/* Content */}
        <div
          className="prose prose-stone max-w-none
            prose-headings:text-stone-900 prose-p:text-stone-600 prose-p:leading-relaxed
            prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
            prose-img:rounded-lg prose-img:border prose-img:border-stone-100
            prose-ul:text-stone-600 prose-ol:text-stone-600
            prose-li:text-stone-600 prose-strong:text-stone-800"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="mt-10 pt-6 border-t border-stone-100">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-stone-400" />
              {article.tags.map(tag => (
                <span key={tag} className="text-xs bg-stone-100 text-stone-500 px-2.5 py-1 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
